/**
 * BrokerageReferral — public landing for the brokerage affiliate
 * program. Higher base split (35%) + auto-promotes to 40% at 10+ active
 * referrals. Routes:
 *   /brokerages       — landing + application form + leaderboard
 *
 * Reuses the existing PartnerDashboard (at /partners/:code?t=...) for
 * post-signup self-service since brokerages are stored as partners with
 * channel='brokerage'.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, DollarSign, Users, TrendingUp, Sparkles, Loader2, ExternalLink, Trophy, Building2, Shield, Briefcase, Check, Copy } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];

function CopyBtn({ text }) {
  const [done, setDone] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); }} data-testid="brokerage-copy-btn" className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] uppercase tracking-wide font-bold text-emerald-700 hover:bg-emerald-50">
      {done ? <Check className="w-3 h-3"/> : <Copy className="w-3 h-3"/>}{done ? "Copied" : "Copy"}
    </button>
  );
}

function BrokerageLeaderboard() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    fetch(`${API}/api/brokerages/leaderboard?limit=10`).then((r) => r.ok ? r.json() : null).then((d) => setItems(d?.items || []));
  }, []);
  if (items.length === 0) return null;
  return (
    <section className="mt-12 max-w-3xl mx-auto" data-testid="brokerage-leaderboard">
      <h2 className="text-2xl font-extrabold text-slate-900 mb-2 flex items-center gap-2"><Trophy className="w-6 h-6 text-amber-500"/> Top brokerages this month</h2>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-widest font-bold text-slate-500"><tr><th className="px-4 py-2 text-left">#</th><th className="px-4 py-2 text-left">Brokerage</th><th className="px-4 py-2 text-left">Province</th><th className="px-4 py-2 text-right">Referrals</th><th className="px-4 py-2 text-right">Earned</th></tr></thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.rank} className="border-t border-slate-100">
                <td className="px-4 py-2 font-bold">#{b.rank}</td>
                <td className="px-4 py-2 font-bold text-slate-900">{b.brokerage_name}</td>
                <td className="px-4 py-2 text-slate-700">{b.province}</td>
                <td className="px-4 py-2 text-right tabular-nums font-bold">{b.conversions}</td>
                <td className="px-4 py-2 text-right tabular-nums font-bold text-emerald-700">${b.earned_cad.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function BrokerageApplyForm() {
  const [form, setForm] = useState({
    brokerage_name: "", contact_name: "", email: "", phone: "",
    broker_license_id: "", province: "ON", agent_count: "",
    target_audience: "both", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const body = { ...form, agent_count: form.agent_count ? Number(form.agent_count) : null };
      const r = await fetch(`${API}/api/brokerages/apply`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`);
      setResult(d);
      try {
        const g = await import("@/lib/google-tag");
        g.fireLeadFormSubmit({
          source: "brokerage_apply", email: form.email.trim(), valueCad: 200,
          transactionId: `brokerage_${d?.code || ""}`,
        });
      } catch { /* never block UX */ }
    } catch (e) { setError(String(e.message || e)); } finally { setBusy(false); }
  }

  if (result) {
    return (
      <div className="rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="brokerage-apply-success">
        <Sparkles className="w-10 h-10 text-emerald-600 mx-auto mb-3"/>
        <h2 className="text-2xl font-extrabold text-slate-900 mb-2">Welcome aboard 🤝</h2>
        <p className="text-slate-700 mb-2">Your brokerage code is live. Start earning <b>{result.split_percent}% recurring</b> on every customer your team refers.</p>
        <p className="text-xs text-slate-600 mb-4">Bonus: hit <b>{result.bonus_threshold} active referrals</b> and your split auto-bumps to <b>{result.bonus_split_percent}%</b>.</p>
        <div className="inline-flex items-center gap-2 px-4 py-3 bg-white rounded-lg border border-slate-200 mb-4">
          <code className="text-sm font-mono font-bold text-slate-900" data-testid="brokerage-share-url">{result.share_url}</code>
          <CopyBtn text={result.share_url}/>
        </div>
        <div className="text-xs text-slate-500">Code: <b className="font-mono">{result.code}</b> · Status: <b>{result.status}</b></div>
        {result.dashboard_url && (
          <a href={result.dashboard_url} className="inline-flex items-center gap-1 mt-4 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-bold hover:bg-slate-800" data-testid="brokerage-open-dashboard">
            <ExternalLink className="w-4 h-4"/> Open your dashboard
          </a>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="brokerage-apply-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Brokerage name *</label>
          <input required value={form.brokerage_name} onChange={(e) => setForm({ ...form, brokerage_name: e.target.value })} data-testid="brokerage-name" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Your name *</label>
          <input required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="brokerage-contact-name" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Email *</label>
          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="brokerage-email" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Phone</label>
          <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="brokerage-phone" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Province *</label>
          <select value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} data-testid="brokerage-province" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white outline-none focus:border-emerald-500">
            {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Agent count</label>
          <input type="number" min={0} value={form.agent_count} onChange={(e) => setForm({ ...form, agent_count: e.target.value })} data-testid="brokerage-agents" placeholder="e.g. 25" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">License # (optional)</label>
          <input value={form.broker_license_id} onChange={(e) => setForm({ ...form, broker_license_id: e.target.value })} data-testid="brokerage-license" placeholder="e.g. RECO 4801234" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Who will you refer?</label>
        <select value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} data-testid="brokerage-audience" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white outline-none focus:border-emerald-500">
          <option value="landlords">Mostly landlords / owner-operators</option>
          <option value="investors">Mostly investors / portfolio LPs</option>
          <option value="both">Both — full ecosystem</option>
        </select>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Anything we should know? (optional)</label>
        <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="brokerage-notes" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
      </div>
      {error && <div className="text-xs text-rose-600">{error}</div>}
      <button type="submit" disabled={busy} data-testid="brokerage-submit" className="w-full px-4 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Generating your code…</> : <><Briefcase className="w-4 h-4"/> Activate brokerage partnership</>}
      </button>
    </form>
  );
}

export default function BrokerageReferral() {
  useEffect(() => { document.title = "Brokerage Affiliate Program — Central Rentals"; }, []);
  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-emerald-50 to-blue-50" data-testid="brokerage-page">
      <header className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="text-sm font-bold text-slate-900">← Central Rentals</Link>
        <Link to="/brokerages#leaderboard" className="text-xs text-slate-600 hover:text-slate-900">Leaderboard</Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 pb-16">
        <section className="text-center pt-8 pb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold mb-4">
            <Building2 className="w-3.5 h-3.5"/> BROKERAGE AFFILIATE PROGRAM
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-4 leading-tight">
            Refer your network.<br/>Earn <span className="text-emerald-600">35–40%</span> recurring.
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            For Canadian brokerages, team leads, and broker-owners. Refer landlords, investors, or fellow agents to Canada's per-unit-priced PMS — earn <b>35% recurring</b> on every subscription. Hit 10 active referrals and auto-promote to <b>40%</b>.
          </p>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto mb-12">
          <div className="rounded-xl bg-white border border-slate-200 p-5 text-center" data-testid="brokerage-benefit-1">
            <DollarSign className="w-7 h-7 text-emerald-600 mx-auto mb-2"/>
            <div className="font-extrabold text-slate-900 mb-1">35% recurring</div>
            <div className="text-xs text-slate-500">Bumps to 40% after 10 active referrals</div>
          </div>
          <div className="rounded-xl bg-white border border-slate-200 p-5 text-center" data-testid="brokerage-benefit-2">
            <Users className="w-7 h-7 text-emerald-600 mx-auto mb-2"/>
            <div className="font-extrabold text-slate-900 mb-1">Whole ecosystem</div>
            <div className="text-xs text-slate-500">Refer landlords, investors, fellow agents — all attribute back</div>
          </div>
          <div className="rounded-xl bg-white border border-slate-200 p-5 text-center" data-testid="brokerage-benefit-3">
            <Shield className="w-7 h-7 text-emerald-600 mx-auto mb-2"/>
            <div className="font-extrabold text-slate-900 mb-1">Verified, paid in CAD</div>
            <div className="text-xs text-slate-500">Non-cash subscription-credit rewards (no cash payouts)</div>
          </div>
        </section>

        <section className="max-w-2xl mx-auto bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm">
          <h2 className="text-2xl font-extrabold text-slate-900 mb-1 text-center">Activate in 60 seconds</h2>
          <p className="text-xs text-slate-500 text-center mb-5">No contract. No minimum. Pause anytime.</p>
          <BrokerageApplyForm/>
        </section>

        <section><BrokerageLeaderboard/></section>

        <section className="mt-16 max-w-3xl mx-auto text-center">
          <h3 className="text-2xl font-extrabold text-slate-900 mb-2">Already a partner?</h3>
          <p className="text-slate-600 mb-4">Use your bookmarked dashboard link to manage referrals and payouts.</p>
        </section>
      </main>
    </div>
  );
}
